import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import clearAllProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearAllProductFilters";
import removeProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/RemoveProductFilters";
import { getProductListDataView } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AttributeTypeFacetModel,
    AttributeValueFacetModel,
    CategoryFacetModel,
    FacetModel,
    PriceFacetModel,
    PriceRangeModel,
} from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { ProductListPageDataContext } from "@insite/content-library/Pages/ProductListPage";
import { formatPriceRangeFacet } from "@insite/content-library/Widgets/ProductList/ProductListPriceFilters";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tag, { TagPresentationProps } from "@insite/mobius/Tag";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    const {
        pages: {
            productList: { productFilters, unfilteredApiParameter, filteredApiParameter },
        },
        context: {
            session: { currency, language },
        },
    } = state;
    if (getProductListDataView(state).value) {
        const unfilteredDataView = getProductsDataView(state, unfilteredApiParameter);
        const unfilteredProductCollection = unfilteredDataView.value ? unfilteredDataView : undefined;

        const filteredDataView = getProductsDataView(state, filteredApiParameter);
        const filteredProductCollection = filteredDataView.value ? filteredDataView : undefined;
        const mergeAttributeTypeFacets = (
            unfilteredAttributeTypeFacets?: AttributeTypeFacetModel[] | null,
            filteredAttributeTypeFacets?: AttributeTypeFacetModel[] | null,
        ) => {
            if (!unfilteredAttributeTypeFacets) {
                return filteredAttributeTypeFacets;
            }

            if (!filteredAttributeTypeFacets) {
                return unfilteredAttributeTypeFacets;
            }

            // XNG-Change: Merge attribute VALUES within each type to include selected values from filtered response
            const mergedTypes = unfilteredAttributeTypeFacets.map(unfilteredType => {
                const filteredType = filteredAttributeTypeFacets.find(
                    f => f.attributeTypeId === unfilteredType.attributeTypeId,
                );

                if (!filteredType?.attributeValueFacets?.length) {
                    return unfilteredType;
                }

                const unfilteredValues = unfilteredType.attributeValueFacets || [];
                const additionalFilteredValues = filteredType.attributeValueFacets.filter(
                    filteredValue =>
                        !unfilteredValues.some(
                            unfilteredValue => unfilteredValue.attributeValueId === filteredValue.attributeValueId,
                        ),
                );

                if (additionalFilteredValues.length === 0) {
                    return unfilteredType;
                }

                return {
                    ...unfilteredType,
                    attributeValueFacets: [...unfilteredValues, ...additionalFilteredValues],
                };
            });

            const typeOnlyInFiltered = filteredAttributeTypeFacets.filter(
                filteredType =>
                    !unfilteredAttributeTypeFacets.some(
                        unfilteredType => unfilteredType.attributeTypeId === filteredType.attributeTypeId,
                    ),
            );

            return [...mergedTypes, ...typeOnlyInFiltered];
        };

        return {
            loaded: true,
            stockedItemsOnly: productFilters.stockedItemsOnly ?? false,
            previouslyPurchasedProducts: productFilters.previouslyPurchasedProducts ?? false,
            brandFacets: unfilteredProductCollection?.brandFacets || filteredProductCollection?.brandFacets,
            productLineFacets:
                unfilteredProductCollection?.productLineFacets || filteredProductCollection?.productLineFacets,
            categoryFacets: unfilteredProductCollection?.categoryFacets,
            priceFacets:
                unfilteredProductCollection?.priceRange?.priceFacets ||
                filteredProductCollection?.priceRange?.priceFacets,
            attributeTypeFacets: mergeAttributeTypeFacets(
                unfilteredProductCollection?.attributeTypeFacets,
                filteredProductCollection?.attributeTypeFacets,
            ),
            searchWithinQueries: productFilters.searchWithinQueries,
            selectedBrandIds: productFilters.brandIds,
            selectedProductLineIds: productFilters.productLineIds,
            selectedPriceFilters: productFilters.priceFilters,
            selectedAttributeValuesIds: productFilters.attributeValueIds,
            selectedCategoryId: productFilters.categoryId,
            currency,
            language,
        };
    }
    return {
        loaded: false,
        stockedItemsOnly: false,
        previouslyPurchasedProducts: false,
        brandFacets: [] as FacetModel[],
        productLineFacets: [] as FacetModel[],
        categoryFacets: [] as CategoryFacetModel[],
        priceFacets: [] as PriceFacetModel[],
        attributeTypeFacets: [] as AttributeTypeFacetModel[],
        searchWithinQueries: [] as string[],
        selectedBrandIds: [] as string[],
        selectedProductLineIds: [] as string[],
        selectedPriceFilters: [] as string[],
        selectedAttributeValuesIds: [] as string[],
        selectedCategoryId: "",
        currency: null,
        language: null,
    };
};

const mapDispatchToProps = {
    removeProductFilters,
    clearAllProductFilters,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & any;

export interface ProductListFilterListStyles {
    wrapper?: InjectableCss;
    skipFiltersButtonWrapper?: InjectableCss;
    skipFiltersButton?: SkipNavStyles;
    filterText?: TypographyPresentationProps;
    filterTag?: TagPresentationProps;
    clearAllLink?: LinkPresentationProps;
    // XNG-Change
    title?: TypographyPresentationProps;
    innerWrapper?: InjectableCss;
    label?: TypographyPresentationProps;
}

export const filterListStyles: ProductListFilterListStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
        `,
    },
    filterText: {
        variant: "h3",
    },
    skipFiltersButtonWrapper: {
        css: css`
            position: relative;
            top: -65px;
            width: 100%;
        `,
    },
    clearAllLink: {
        css: css`
            padding-bottom: 20px;
        `,
    },
};

const styles = filterListStyles;

const ProductListFilterList: FC<Props> = ({
    loaded,
    stockedItemsOnly,
    previouslyPurchasedProducts,
    brandFacets,
    productLineFacets,
    priceFacets,
    categoryFacets,
    attributeTypeFacets,
    searchWithinQueries,
    selectedBrandIds,
    selectedProductLineIds,
    selectedPriceFilters,
    selectedAttributeValuesIds,
    selectedCategoryId,
    removeProductFilters,
    clearAllProductFilters,
    currency,
    language,
}) => {
    if (!loaded) {
        return null;
    }

    const clickRemoveStockedItemsOnly = () => {
        removeProductFilters({ stockedItemsOnly: false });
    };

    const clickRemovePreviouslyPurchasedProducts = () => {
        removeProductFilters({ previouslyPurchasedProducts: false });
    };

    const clickRemoveSearchHandler = (search: string) => {
        removeProductFilters({ searchWithinQueries: [search] });
    };

    const clickRemoveBrandHandler = (id: string) => {
        removeProductFilters({ brandIds: [id] });
    };

    const clickRemoveProductLineHandler = (id: string) => {
        removeProductFilters({ productLineIds: [id] });
    };

    const clickRemovePriceHandler = (price: string) => {
        removeProductFilters({ priceFilters: [price] });
    };

    const clickRemoveAttributeValueHandler = (id: string) => {
        removeProductFilters({ attributeValueIds: [id] });
    };

    const clickRemoveCategoryHandler = () => {
        removeProductFilters({ categoryId: selectedCategoryId });
    };

    const anyActiveFilters =
        searchWithinQueries?.length ||
        stockedItemsOnly ||
        previouslyPurchasedProducts ||
        (brandFacets && selectedBrandIds?.length) ||
        (productLineFacets && selectedProductLineIds?.length) ||
        (priceFacets && selectedPriceFilters?.length) ||
        (attributeTypeFacets && selectedAttributeValuesIds?.length) ||
        selectedCategoryId;

    // XNG-Change: some changes on structure
    return (
        <StyledWrapper {...styles.wrapper}>
            {anyActiveFilters && (
                <>
                    <Typography {...styles.title}>{translate("Search Terms")}</Typography>
                    <StyledWrapper {...styles.innerWrapper}>
                        <StyledWrapper {...styles.skipFiltersButtonWrapper}>
                            <ProductListPageDataContext.Consumer>
                                {({ ref }) => {
                                    return ref ? (
                                        <SkipNav
                                            text={translate("Skip to Results")}
                                            extendedStyles={styles.skipFiltersButton}
                                            destination={ref}
                                        />
                                    ) : undefined;
                                }}
                            </ProductListPageDataContext.Consumer>
                        </StyledWrapper>
                        {previouslyPurchasedProducts && (
                            <Tag
                                className="tag-option"
                                {...styles.filterTag}
                                onDelete={clickRemovePreviouslyPurchasedProducts}
                                data-test-selector="previouslyPurchasedProductsFilter"
                            >
                                {translate("Previously Purchased Only")}
                            </Tag>
                        )}
                        {stockedItemsOnly && (
                            <Tag
                                className="tag-option"
                                {...styles.filterTag}
                                onDelete={clickRemoveStockedItemsOnly}
                                data-test-selector="stockedItemsOnlyFilter"
                            >
                                {translate("Stocked Items Only")}
                            </Tag>
                        )}
                        {searchWithinQueries?.map((s: string) => (
                            <Tag
                                className="tag-option"
                                key={s}
                                {...styles.filterTag}
                                onDelete={() => clickRemoveSearchHandler(s)}
                                data-test-selector="searchWithinFilter"
                            >
                                <Typography {...styles.label}>{translate("Search")}</Typography>: {s}
                            </Tag>
                        ))}
                        {brandFacets &&
                            selectedBrandIds?.map((id: string) => (
                                <Tag
                                    className="tag-option"
                                    key={id}
                                    {...styles.filterTag}
                                    onDelete={() => clickRemoveBrandHandler(id)}
                                    data-test-selector={`brandFilter${id}`}
                                >
                                    <Typography {...styles.label}> {translate("Brand")} </Typography>:{" "}
                                    {brandFacets?.find((o: any) => o.id === id)?.name}
                                </Tag>
                            ))}
                        {productLineFacets &&
                            selectedProductLineIds?.map((id: string) => (
                                <Tag
                                    className="tag-option"
                                    key={id}
                                    {...styles.filterTag}
                                    onDelete={() => clickRemoveProductLineHandler(id)}
                                    data-test-selector={`productLineFilter${id}`}
                                >
                                    <Typography {...styles.label}>{translate("Product Line")}: </Typography>
                                    {productLineFacets?.find((o: any) => o.id === id)?.name}
                                </Tag>
                            ))}

                        {selectedCategoryId && (
                            <Tag
                                className="tag-option"
                                {...styles.filterTag}
                                onDelete={() => clickRemoveCategoryHandler()}
                                data-test-selector={`categoryFilter${selectedCategoryId}`}
                            >
                                <Typography {...styles.label}>{translate("Category")}</Typography>:{" "}
                                {
                                    categoryFacets?.find((o: CategoryFacetModel) => o.categoryId === selectedCategoryId)
                                        ?.shortDescription
                                }
                            </Tag>
                        )}
                        {priceFacets &&
                            selectedPriceFilters?.map((id: string) => (
                                <Tag
                                    className="tag-option"
                                    key={id}
                                    {...styles.filterTag}
                                    onDelete={() => clickRemovePriceHandler(id)}
                                    data-test-selector={`priceFilter${id}`}
                                >
                                    <Typography {...styles.label}>{translate("Price")}</Typography>:{" "}
                                    {formatPriceRangeFacet(
                                        priceFacets?.find((o: PriceRangeModel) => o.minimumPrice.toString() === id),
                                        currency,
                                        language,
                                    )}
                                </Tag>
                            ))}
                        {attributeTypeFacets &&
                            selectedAttributeValuesIds?.map((id: string) => {
                                const attributeTypeFacet = attributeTypeFacets?.find((at: AttributeTypeFacetModel) =>
                                    at.attributeValueFacets?.find(av => av.attributeValueId === id),
                                );
                                const attributeValueFacet = attributeTypeFacet?.attributeValueFacets?.find(
                                    (av: AttributeValueFacetModel) => av.attributeValueId === id,
                                );

                                return (
                                    <Tag
                                        className="tag-option"
                                        key={id}
                                        {...styles.filterTag}
                                        onDelete={() => clickRemoveAttributeValueHandler(id)}
                                        data-test-selector={`attributeValueFilter${id}`}
                                    >
                                        <Typography {...styles.label}>{attributeTypeFacet?.nameDisplay}</Typography>:{" "}
                                        {attributeValueFacet?.valueDisplay}
                                    </Tag>
                                );
                            })}
                        <Link
                            {...styles.clearAllLink}
                            onClick={() => clearAllProductFilters({ coveoClearAll: true })}
                            data-test-selector="productListClearAll"
                        >
                            {translate("Clear All")}
                        </Link>
                    </StyledWrapper>
                </>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListFilterList),
    definition: {
        group: "Product List",
        displayName: "Filter List",
        allowedContexts: ["ProductListPage", "CategoryDetailsPage"],
    },
};

export default widgetModule;
